import React, {useContext, useState} from 'react';
import {Button, Typography, Alert, Select, DatePicker, Space, Checkbox} from 'antd';
import {useMergeEventBlockMainData, useMergeBlockMainData} from "../../../logs/useTableData";
import {useMailTitle} from '../../../country/useMailTitle';
import {PlayerContext} from '../../../../shared/contexts/playerContext';
import {internalApi} from '../../../../common/axios';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import IsLoading from "../../../../shared/components/IsLoading";
import ErrorPage from "../../../../shared/components/ErrorPage";
import CustomMergeEventGrid from '../mergeEvent/CustomMergeEventGrid';
import {useItemKrTextData} from "../../../probability/useItemData";
import {AuthContext} from "../../../../shared/contexts/authContext";
import {useMergeBlockImageMap, resolveMergeBlockImageUrl} from "../../../../shared/hooks/useMergeBlockImageMap";

const {Text} = Typography;
const {Option} = Select;

const isCurrencyItem = (index) =>
    (index >= 300101 && index <= 300110) ||
    (index >= 300201 && index <= 300210) ||
    (index >= 300301 && index <= 300310) ||
    (index >= 300401 && index <= 300410);

const resolveCurrencyIndex = (index) => {
    if (index >= 300401 && index <= 300410) return index - 300000 + 2000;
    return index - 300000;
};

const MergeEventBugCompensationPage = () => {
    const {currentUser} = useContext(AuthContext);

    const [jsonInput, setJsonInput] = useState('');
    const [boardData, setBoardData] = useState(null);
    const [error, setError] = useState('');
    const [rewardBlocks, setRewardBlocks] = useState([]);
    const [mail, setMail] = useState({title: '', expired: 3});
    const [isOverwriteMode, setIsOverwriteMode] = useState(false);
    const [filterCurrencyOnly, setFilterCurrencyOnly] = useState(false);

    const displayBlocks = filterCurrencyOnly ? rewardBlocks.filter(isCurrencyItem) : rewardBlocks;

    const {data: mergeEventMainData, isLoading, error: mergeEventBlockMainDataError} = useMergeEventBlockMainData();
    const {data: mergeBlockData} = useMergeBlockMainData();
    const {data: mailTitle, isLoading: isMailTitleLoading, error: mailTitleError} = useMailTitle();
    const {data: krItemTextData, isLoading: isKrItemTextData, error: krItemTextDataError} = useItemKrTextData();
    const {data: imageMap} = useMergeBlockImageMap();
    const {playerData} = React.useContext(PlayerContext);

    // JSON 입력 핸들러
    const handleInputChange = (e) => {
        setJsonInput(e.target.value);
        setError('');
    };

    // JSON 파싱 및 보상 블록 추출
    const handleConfirm = () => {
        try {
            const parsed = JSON.parse(jsonInput);
            let data = parsed[0];
            if (data['ActiveMergeEventState'] === undefined) {
                data['ActiveMergeEventState'] = parsed[0];
            }
            setBoardData(data);
            setError('');
            // 보상 블록 추출
            const slotData = data?.ActiveMergeEventState?.boardData?.slotData || [];
            const blockDepot = data?.ActiveMergeEventState?.boardData?.blockDepot || [];
            let blockIndexList = [];
            // slotData 이중배열에서 조건에 맞는 blockIndex 추출
            slotData.forEach(row => {
                row.forEach(cell => {
                    if (cell?.eventBlock && cell.eventBlock.IsBlockState_AnyBox === false) {
                        blockIndexList.push(cell.eventBlock.blockIndex);
                    }
                });
            });
            // blockDepot의 blockIndex 추가
            blockDepot.forEach(obj => {
                if (obj?.blockIndex !== undefined) {
                    blockIndexList.push(obj.blockIndex);
                }
            });

            setRewardBlocks(blockIndexList);
        } catch (e) {
            setError('유효한 JSON 형식이 아닙니다.');
        }
    };

    // 현재 플레이어 데이터에서 보상 블록 추출 후 덮어쓰기 모드 진입
    const handleOverwrite = () => {
        if (!jsonInput) {
            toast.error('덮어쓸 JSON 데이터를 먼저 입력하세요.');
            return;
        }
        try {
            JSON.parse(jsonInput); // 유효성 검사만
        } catch (e) {
            setError('유효한 JSON 형식이 아닙니다.');
            return;
        }

        const data = playerData?.playerInfo?.mergeEventData;
        if (!data) {
            toast.error('플레이어 머지이벤트 데이터가 없습니다.');
            return;
        }

        const slotData = data?.ActiveMergeEventState?.boardData?.slotData || [];
        const blockDepot = data?.ActiveMergeEventState?.boardData?.blockDepot || [];
        const blockIndexList = [];

        slotData.forEach(row => {
            row.forEach(cell => {
                if (cell?.eventBlock && cell.eventBlock.IsBlockState_AnyBox === false) {
                    blockIndexList.push(cell.eventBlock.blockIndex);
                }
            });
        });
        blockDepot.forEach(obj => {
            if (obj?.blockIndex !== undefined) {
                blockIndexList.push(obj.blockIndex);
            }
        });

        const parsed = JSON.parse(jsonInput);
        let jsonData = parsed[0];
        if (jsonData['ActiveMergeEventState'] === undefined) {
            jsonData = {ActiveMergeEventState: jsonData};
        }
        setBoardData(jsonData);
        setRewardBlocks(blockIndexList);
        setIsOverwriteMode(true);
        toast.success('현재 데이터에서 보상 블록이 추출되었습니다. 우편 전송 후 덮어쓰기가 진행됩니다.');
    };

    // JSON 입력 데이터로 실제 덮어쓰기 API 호출
    const executeOverwrite = async () => {
        const parsed = JSON.parse(jsonInput);
        const jsonData = parsed[0];
        jsonData.id = jsonData.backupID;
        jsonData.isActive = true;
        jsonData.isOpened = true;

        const playInfo = {
            ...playerData.playerInfo,
            mergeEventData: {
                ...playerData.playerInfo.mergeEventData,
                ActiveMergeEventState: jsonData,
            }
        };

        await internalApi.post('/player/playInfo', {
            playInfo,
            userKey: playerData.userKey,
            syncId: playerData.syncId,
        });
    };

    // 메일 제목 변경
    const handleTitleChange = (value) => {
        setMail(prevMail => ({...prevMail, title: value}));
    };
    // 만료일 변경
    const handleDateChange = (date) => {
        if (date) {
            const expiredDays = date.endOf('day').diff(dayjs().startOf('day'), 'day');
            setMail(prevMail => ({...prevMail, expired: expiredDays}));
        }
    };
    // 우편 전송 핸들러 (덮어쓰기 모드일 경우 덮어쓰기 API도 함께 호출)
    const handleSendMail = async () => {
        const hasItems = displayBlocks.length > 0;

        // 보상 아이템이 없으면 덮어쓰기 모드일 때만 우편 전송 없이 진행 가능
        if (!hasItems && !isOverwriteMode) {
            toast.error('지급할 아이템이 없습니다.');
            return;
        }
        if (hasItems && !mail.title) {
            toast.error('메일 제목을 선택하세요');
            return;
        }
        try {
            if (hasItems) {
                const items = displayBlocks.map(blockIndex => {
                    if (filterCurrencyOnly) {
                        const resolvedIndex = resolveCurrencyIndex(blockIndex);
                        const tbl = mergeBlockData?.[resolvedIndex];
                        return {id: tbl.itemIdx, type: tbl.itemType, quantity: tbl.itemValue};
                    }
                    const block = krItemTextData[currentUser.target].find(item => item.index === blockIndex);
                    return {id: block.index, type: 7, quantity: 1};
                });
                const mailData = {
                    userKey: playerData.userKey,
                    mail: {...mail, items},
                };
                await internalApi.post('player/mail', mailData);
                toast.success('메일이 성공적으로 발송되었습니다.');
            }

            if (isOverwriteMode) {
                await executeOverwrite();
                toast.success('데이터 덮어쓰기가 완료되었습니다.');
                setIsOverwriteMode(false);
            }

            setMail({title: '', expired: 3});
            setRewardBlocks([]);
            setJsonInput('');
        } catch (err) {
            toast.error(isOverwriteMode ? '우편 전송 또는 덮어쓰기 실패!' : '메일 발송 실패!');
        }
    };

    // 블록 인덱스를 이미지로 매핑
    const renderBlockImages = () => {
        if (!rewardBlocks.length) return <Text>지급할 아이템이 없습니다.</Text>;
        if (!displayBlocks.length) return <Text>재화 아이템이 없습니다.</Text>;
        return (
            <div style={{display: 'flex', flexWrap: 'wrap', gap: 12, margin: '16px 0'}}>
                {displayBlocks.map((blockIndex, idx) => {
                    const blockDetails = mergeEventMainData?.[blockIndex];
                    const imageUrl = resolveMergeBlockImageUrl(blockDetails?.resourcePath, imageMap);
                    return (
                        <div key={idx} style={{textAlign: 'center'}}>
                            <img src={imageUrl} alt={`block-${blockIndex}`}
                                 style={{width: 48, height: 48, objectFit: 'contain'}}/>
                            <div style={{fontSize: 12, marginTop: 4}}>Index: {filterCurrencyOnly ? resolveCurrencyIndex(blockIndex) : blockIndex}</div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const isAnyLoading = isLoading || isMailTitleLoading || isKrItemTextData;
    const isAnyError = mergeEventBlockMainDataError || mailTitleError || krItemTextDataError;
    if (isAnyLoading) return <IsLoading/>;
    if (isAnyError) return <ErrorPage error={mergeEventBlockMainDataError}/>;

    return (
        <div style={{maxWidth: 1100, margin: '0 auto', padding: 24}}>
            <h2>머지이벤트 버그 보상 지급</h2>
            <Alert
                message="boardData JSON을 입력하면 보상 아이템이 추출됩니다."
                type="info"
                style={{marginBottom: 16}}
            />
            <div style={{display: 'flex', gap: 24}}>
                <div style={{flex: 1, display: 'flex', flexDirection: 'column'}}>
                    <textarea
                        value={jsonInput}
                        onChange={handleInputChange}
                        rows={10}
                        placeholder="여기에 boardData JSON을 입력하세요"
                        style={{width: '100%', marginBottom: 8}}
                    />
                    <Space style={{marginBottom: 16}}>
                        <Button onClick={handleConfirm} type="primary">
                            보상 추출
                        </Button>
                        <Button onClick={handleOverwrite} danger>
                            데이터 덮어쓰기
                        </Button>
                    </Space>
                    {error && <div style={{color: 'red', marginBottom: 8}}>{error}</div>}
                    {rewardBlocks.length > 0 && (
                        <Checkbox
                            checked={filterCurrencyOnly}
                            onChange={(e) => setFilterCurrencyOnly(e.target.checked)}
                            style={{marginBottom: 8}}
                        >
                            재화 아이템만 보기 ({rewardBlocks.filter(isCurrencyItem).length}개)
                        </Checkbox>
                    )}
                    {renderBlockImages()}
                    {(rewardBlocks.length > 0 || isOverwriteMode) && (
                        <Space direction="vertical" style={{width: '100%', marginTop: 24}}>
                            {displayBlocks.length > 0 && (
                                <>
                                    <Select
                                        placeholder="메일 제목을 선택하세요"
                                        value={mail.title}
                                        onChange={handleTitleChange}
                                        size="large"
                                        style={{width: '100%'}}
                                        loading={isMailTitleLoading}
                                        disabled={isMailTitleLoading || !mailTitle}
                                        showSearch
                                        optionLabelProp="label"
                                    >
                                        {mailTitle && mailTitle.map((option) => {
                                            const koTitle = option.title.find((t) => t.langCode === 'ko')?.text || 'Unknown';
                                            return (
                                                <Option key={option.index} value={option.index} label={koTitle}>
                                                    <div>
                                                        <div style={{fontWeight: 'bold'}}>{koTitle}</div>
                                                        <div style={{fontSize: '11px', color: '#999'}}>ID: {option.index}</div>
                                                    </div>
                                                </Option>
                                            );
                                        })}
                                    </Select>
                                    <DatePicker
                                        value={dayjs().endOf('day').add(mail.expired, 'day')}
                                        onChange={handleDateChange}
                                        size="large"
                                        style={{width: '100%'}}
                                        format="YYYY-MM-DD"
                                        disabledDate={(current) => current && current < dayjs().startOf('day')}
                                    />
                                </>
                            )}
                            <Button onClick={handleSendMail} type="primary" danger={isOverwriteMode}>
                                {isOverwriteMode
                                    ? (displayBlocks.length > 0 ? '우편 전송 및 덮어쓰기' : '덮어쓰기 실행')
                                    : '우편 전송하기'}
                            </Button>
                        </Space>
                    )}
                </div>
                <div style={{flex: 1}}>
                    <CustomMergeEventGrid mergeEventData={boardData}/>
                </div>
            </div>
        </div>
    );
};

export default MergeEventBugCompensationPage;